import { VendorSource } from "@/core";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo } from "react";
import { useForm, type UseFormProps } from "react-hook-form";
import { z } from "zod";

export interface ContactFormValues {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  secondaryPhone?: string;
  secondaryEmail?: string;
  companyName?: string;
  type?: "travel agent" | "broker" | "private" | "family";
  source?: VendorSource;
}

type ContactFormSchema = z.ZodType<ContactFormValues, unknown>;

interface UseContactFormProps
  extends Omit<UseFormProps<ContactFormValues>, "mode" | "resolver"> {
  contact?: Partial<ContactFormValues> | null;
  mode: "create" | "edit";
  schema: ContactFormSchema;
}

export function useContactForm({
  contact,
  mode,
  schema,
  ...props
}: UseContactFormProps) {
  const defaultValues = useMemo<ContactFormValues>(
    () => ({
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      secondaryPhone: "",
      secondaryEmail: "",
      companyName: "",
      type: undefined,
      source: undefined,
    }),
    [],
  );

  const form = useForm<ContactFormValues>({
    ...props,
    resolver: zodResolver(schema as never),
    defaultValues: props.defaultValues || defaultValues,
  });

  const { reset } = form;

  useEffect(() => {
    if (mode === "edit" && contact) {
      reset({
        firstName: contact.firstName || "",
        lastName: contact.lastName || "",
        email: contact.email || "",
        phone: contact.phone || "",
        secondaryPhone: contact.secondaryPhone || "",
        secondaryEmail: contact.secondaryEmail || "",
        companyName: contact.companyName || "",
        type: contact.type,
        source: contact.source,
      });
    } else if (mode === "create") {
      reset(defaultValues);
    }
  }, [contact, defaultValues, reset, mode]);

  return form;
}
